import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import Helpers from "utils/Helpers"
import L10n from "lib/carrot-l10n"
import { reportErrorMessage } from "utils/ErrorReporting"
import { CardExpenseStatus, ExpenseStatus } from "utils/ExpenseStatuses"
import { getCardState } from "reducers/userInfo"
import { Spacer } from "components/views/atoms/Atoms"
import { useSelector } from "react-redux"
import CardStates from "utils/CardStates"
import { MultiPathSVG, SinglePathSVG } from "components/views/SVG"
import { getCurrencySymbol } from "reducers/companyInfo"
import {
  getCompletedReimbursementsList,
  getPendingReimbursementsList,
  getReimbursementsRequiringActionList
} from "derivedSelectors/reimbursementsSelectors"
import { getReimbursementsList } from "reducers/reimbursements"
import { BodySmall } from "components/views/atoms/Bodies"
import { TextLink } from "components/views/atoms/links/TextLink"
import { CircleImage } from "components/views/atoms/images/CircleImage"
import Settings from "utils/CarrotConfig"
import { ReimbursementsListPaidStatus } from "components/views/reimbursements/reimbursementsList/ReimbursementsListPaidStatus"
import { ReimbursementsListDeductibleString } from "components/views/reimbursements/reimbursementsList/ReimbursementsListDeductibleString"
import { useToggle } from "../../../../utils/Hooks"
import { ReimbursementsModalContainer } from "../../../containers/reimbursements/ReimbursementsModalContainer"
import { getIsCarrotCardEligible } from "../../../../reducers/userInfo"
import { H2, H4, H5 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const FileDrawingLink = ({ guid }) => (
  <a
    className="mr1 carrot-orange hover-dark-carrot-orange animate-all"
    href={`${Settings.CARROT_BACKEND_URL}/api/v2/statements/${guid}`}
    target="_blank"
    rel="noreferrer"
  >
    <SinglePathSVG icon="statementFile" />
  </a>
)

const Header = ({ children, rightAlign = false }) => (
  <th className="black-60 gotham ttu tl medium" style={{ fontSize: "11px" }}>
    <div className={`mb2 ${rightAlign ? "tr" : null}`}>{children}</div>
  </th>
)

const Legend = () => {
  const isCarrotCardEligible = useSelector(getIsCarrotCardEligible)
  const hasHadCarrotCardExp = useSelector(getReimbursementsList)
  const [hasPrevCarrotCardExpenses, setHasPrevCarrotCardExpenses] = useState(false)

  useEffect(() => {
    const verifyIfHasHadCarrotCardExp = hasHadCarrotCardExp.map((x) => {
      if (x.isCardExpense) {
        if (!hasPrevCarrotCardExpenses) setHasPrevCarrotCardExpenses(true)
      }
    })
  }, [])

  return (
    <div className="fr tr">
      {(isCarrotCardEligible || hasPrevCarrotCardExpenses) && (
        <BodySmall>
          <span>Carrot Card®</span>
          <MultiPathSVG iconName="icn-carrot-card-key" className="dib ml2" />
        </BodySmall>
      )}
      <BodySmall>
        <span>{l`outOfPocket`}</span>
        <MultiPathSVG iconName="icn-oop-key" className="dib ml2" />
      </BodySmall>
    </div>
  )
}

const Attachments = ({ reimbursement }) => {
  const [isReimbursementsModalOpen, toggleIsReimbursementsModalOpen] = useToggle(false)
  if (reimbursement.cardStatus === CardExpenseStatus.PENDING_MEMBER_INFO) {
    return (
      <>
        <Link
          to={undefined}
          className="carrot-orange hover-dark-carrot-orange animate-all"
          onClick={() => toggleIsReimbursementsModalOpen()}
        >
          <MultiPathSVG iconName="btn-upload" />
        </Link>
        {isReimbursementsModalOpen && (
          <ReimbursementsModalContainer
            expenseId={reimbursement.expenseId}
            onClose={toggleIsReimbursementsModalOpen}
            onFinish={toggleIsReimbursementsModalOpen}
          />
        )}
      </>
    )
  } else {
    return (
      <div className="flex flex-wrap flex-row-reverse w3">
        {reimbursement.fileGuids.map((guid) => (
          <FileDrawingLink key={guid} guid={guid} />
        ))}
      </div>
    )
  }
}

const ExpenseTypeIcon = ({ isCardExpense }) => (
  <MultiPathSVG iconName={isCardExpense ? "badge-transaction-card" : "badge-transaction-oop"} />
)

const TableHeader = () => (
  <>
    <Header>{l`client.reimbursements.type`}</Header>
    <Header>{l`client.reimbursements.details`}</Header>
    <Header rightAlign="true">{l`client.reimbursements.statement`}</Header>
  </>
)

const Row = ({ reimbursement }) => (
  <tr className="bb bt bw1 b--black-05">
    <td className="pv3 v-mid" style={{ width: "3em" }}>
      <ExpenseTypeIcon isCardExpense={reimbursement.isCardExpense} />
    </td>
    <td className="pv3 v-mid">
      <Details reimbursement={reimbursement} />
    </td>
    <td className="pv3">
      <div className="fr">
        {reimbursement.isAdjustment ? (
          <BodySmall color="black-60">{l`client.reimbursements.adjustment`}</BodySmall>
        ) : (
          <Attachments reimbursement={reimbursement} />
        )}
      </div>
    </td>
  </tr>
)

const Details = ({ reimbursement }) => {
  const currencySymbol = useSelector(getCurrencySymbol)
  return (
    <div>
      <div className="flex flex-row">
        <div className="flex-column mr3">
          <H5>{Helpers.formatDate(reimbursement.submittedDatetime)}</H5>
        </div>
        <div className="flex-column">{reimbursementAmount({ reimbursement, currencySymbol })}</div>
      </div>
      <BodySmall color="black-60">{renderStatus(reimbursement)}</BodySmall>
    </div>
  )
}

const renderStatus = (reimbursement) => {
  const submitByDate = new Date(reimbursement.submittedDatetime)
  submitByDate.setDate(submitByDate.getDate() + 30)

  if (reimbursement.isCardExpense) {
    switch (reimbursement.cardStatus) {
      case CardExpenseStatus.PENDING_MEMBER_INFO:
        return l`client.reimbursements.requestStatement` + Helpers.formatDate(submitByDate, "MMM DD")
      case CardExpenseStatus.APPROVED:
        return l`client.reimbursements.approvedOn`
      case CardExpenseStatus.DENIED_RECONCILED:
        return l`client.reimbursements.denied`
      case CardExpenseStatus.DENIED_RECONCILIATION_REQUESTED:
        return (
          <>
            <span>{l`client.reimbursements.needsMoreInfo`}</span>
            <TextLink
              color="black-60"
              to="mailto:support@get-carrot.com"
            >{l`client.reimbursements.contactUs`}</TextLink>
          </>
        )
      case CardExpenseStatus.PENDING_CARE_NAV:
        return l`client.reimbursements.inProgress`
      default:
        reportErrorMessage(`unexpected status provided: ${reimbursement.cardStatus}`)
        return reimbursement.cardStatus
    }
  } else {
    switch (reimbursement.status) {
      case ExpenseStatus.RECEIVED:
        return l`client.reimbursements.inProgress`
      case ExpenseStatus.APPROVED:
        return (
          <>
            {l`Approved`}
            <ReimbursementsListDeductibleString reimbursement={reimbursement} />
          </>
        )
      case ExpenseStatus.PAID:
        return <ReimbursementsListPaidStatus reimbursement={reimbursement} />
      case ExpenseStatus.DENIED:
        return l`client.reimbursements.denied`
      default:
        reportErrorMessage(`unexpected status provided: ${reimbursement.status}`)
        return reimbursement.status
    }
  }
}

const reimbursementAmount = ({ reimbursement, currencySymbol }) =>
  !reimbursement.isCardExpense && reimbursement.status === ExpenseStatus.RECEIVED ? (
    <H5>{l(`${currencySymbol}--.--`)}</H5>
  ) : (
    <H5>{Helpers.convertToCurrencyString(reimbursement.amount, currencySymbol)}</H5>
  )

const ReimbursementsListSection = ({ title, items, emptyText }) => {
  const hasItems = items && items.length > 0
  return (
    <div>
      <Spacer height={1} />
      <H4 variant="primary">{title}</H4>
      <Spacer height={1} />
      <table className="w-100 collapse">
        <thead className={hasItems ? "" : "o-40"}>
          <tr>
            <TableHeader />
          </tr>
        </thead>
        <tbody>
          {hasItems ? (
            items.map((item) => <Row key={item.expenseId} reimbursement={item} />)
          ) : (
            <tr>
              <td colSpan="3" className="bb bt bw1 b--black-05 pv3">
                <BodySmall color="black-60 tc">{emptyText}</BodySmall>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

const EmptyReimbursementsList = () => {
  const isCardEligible = useSelector(getCardState) !== CardStates.INELIGIBLE
  return (
    <div>
      <Spacer height={1} />
      <table className="w-100 collapse">
        <thead className="o-40">
          <tr>
            <TableHeader />
          </tr>
        </thead>
        <tbody>
          <tr className="bb bt bw1 b--black-05">
            <td colSpan="3" className="pv3">
              <div className="w-100 flex justify-center pv5">
                <div className="flex flex-column items-center" style={{ maxWidth: "360px" }}>
                  <CircleImage iconName="icn-transaction" size={84} padding={17} backgroundColor="nurse-gray" />
                  <Spacer height={1.5} />
                  <BodySmall align="tc" color="black-60">
                    {isCardEligible
                      ? l`client.reimbursements.emptyWithCard`
                      : l`client.reimbursements.emptyWithoutCard`}
                  </BodySmall>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const ReimbursementsList = () => {
  const reimbursements = useSelector((state) => ({
    reimbursementsCompleted: getCompletedReimbursementsList(state),
    reimbursementsPending: getPendingReimbursementsList(state),
    reimbursementsRequiringAction: getReimbursementsRequiringActionList(state),
    reimbursementsList: getReimbursementsList(state)
  }))

  const {
    reimbursementsRequiringAction,
    reimbursementsCompleted,
    reimbursementsPending,
    reimbursementsList
  } = reimbursements

  return (
    <div>
      <H2 variant="primary">Activity</H2>
      <Legend />
      <Spacer height={0.5} />
      {reimbursementsList.length > 0 ? (
        <>
          <ReimbursementsListSection
            title={l`client.reimbursements.needsAttention`}
            emptyText={l`client.reimbursements.needsAttentionEmpty`}
            items={reimbursementsRequiringAction}
          />
          <ReimbursementsListSection
            title={l`client.reimbursements.pending`}
            emptyText={l`client.reimbursements.pendingEmpty`}
            items={reimbursementsPending}
          />
          <ReimbursementsListSection
            title={l`client.reimbursements.complete`}
            emptyText={l`client.reimbursements.completeEmpty`}
            items={reimbursementsCompleted}
          />
        </>
      ) : (
        <EmptyReimbursementsList />
      )}
    </div>
  )
}

export default ReimbursementsList
